import { Injectable } from '@angular/core';

export type RuntimeMode = 'local' | 'qa' | 'uat' | 'production';

const PRODUCTION_HOST_SET = new Set<string>([
  'profiles.unthsc.edu',
  'profiles.unthealth.edu'
]);

const QA_HOST_SET = new Set<string>([
  'profile.qa.apps.unthsc.edu'
]);

const UAT_HOST_SET = new Set<string>([
  'profile.uat.apps.unthsc.edu'
]);

function normalizeHost(hostname: string | null | undefined): string {
  return (hostname || '').trim().toLowerCase();
}

export function isProductionHost(hostname: string | null | undefined): boolean {
  return PRODUCTION_HOST_SET.has(normalizeHost(hostname));
}

export function resolveRuntimeMode(hostname: string | null | undefined, isServer = false): RuntimeMode {
  const host = normalizeHost(hostname);

  // SSR does not always have an incoming host readily available in app startup.
  if (!host) {
    return isServer ? 'production' : 'local';
  }

  if (isProductionHost(host)) {
    return 'production';
  }

  if (QA_HOST_SET.has(host)) {
    return 'qa';
  }

  if (UAT_HOST_SET.has(host)) {
    return 'uat';
  }

  return 'local';
}

export function isProductionRuntime(hostname: string | null | undefined, isServer = false): boolean {
  return resolveRuntimeMode(hostname, isServer) === 'production';
}

export function getBrowserHostname(): string {
  if (typeof window === 'undefined' || !window.location) {
    return '';
  }

  return normalizeHost(window.location.hostname);
}

@Injectable({ providedIn: 'root' })
export class RuntimeModeService {
  getHostname(): string {
    return getBrowserHostname();
  }

  getMode(): RuntimeMode {
    return resolveRuntimeMode(this.getHostname());
  }

  isProduction(): boolean {
    return this.getMode() === 'production';
  }
}